import { GoogleAuthRedirectButton } from "./auth-fe12.esm.js";
import { AccountFormHeader, AccountFormWrapper, EmailDisclaimerText, FormWrapper } from "./auth-fe15.esm.js";
import AccountEmailForm from "./auth-fe16.esm.js";
import AccountFormSubHeader from "./auth-fe17.esm.js";
import { NamelessAccountContext } from "./auth-fe18.esm.js";
import AccountPasswordForm from "./auth-fe19.esm.js";
import { CheckEmailForm } from "./auth-fe20.esm.js";
import { ErrorPrompt } from "./auth-fe21.esm.js";
import PasswordlessResultPage from "./auth-fe22.esm.js";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Text } from "@opendoor/bricks-next";
//#region src/AccountForm/AccountFormCinderblocks/AccountForm.tsx
var Divider = () => /* @__PURE__ */ React.createElement(Box, {
	height: 1,
	backgroundColor: "$borderSecondary",
	width: "100%",
	my: "$4x"
});
var AccountForm = ({ analyticsName, phone = "", email: initialEmail = "", name: initialName = "", loginHeaderText, registrationHeaderText, loginSubHeaderText, registrationSubHeaderText, emailLoading, passwordLoading, error, phoneNumberInputOptional, phoneNumberInputHidden, openForgotPasswordInNewTab, showGoogleAuth = false, overrideHasSubmittedEmailCheckForLogin, emailMismatch, requestResetPassword, hidePasswordField = false, onPasswordlessLoginRequest, onboardingAccountPageCTA, fullNamePlaceholder, fullNameLabel, disclaimerProps, passwordFormActionText, onHumanStatusRequest, onLogin, onRegister, postEmailCtaSection, unsetContainer, fullNameOptional, fullNameHidden, loginAnalyticsPrefix: googleAuthAnalyticsPrefix, loginRedirectTo, ...props }) => {
	const [email, setEmail] = useState(initialEmail);
	const emailInputRef = useRef(null);
	const passwordInputRef = useRef(null);
	useEffect(() => {
		if (emailInputRef.current) emailInputRef.current.focus();
	}, []);
	const [name, setName] = useState(initialName);
	const [isNewHuman, setIsNewHuman] = useState(!!props.isNewHuman);
	const [hasGeneratedPassword, setHasGeneratedPassword] = useState(!!props.hasGeneratedPassword);
	const [googleLoginAttempt, setGoogleLoginAttempt] = useState(false);
	const [isRequestingPasswordlessLogin, setIsRequestingPasswordlessLogin] = useState(false);
	const [hasPasswordlessLoginRequestSucceeded, setHasPasswordlessLogingRequestSucceeded] = useState();
	const { shouldRegisterOnEmailSubmit, emailRegistrationError, onAccountUpdate } = useContext(NamelessAccountContext);
	const handleLogin = (password) => {
		setGoogleLoginAttempt(false);
		onLogin(email, password);
	};
	const handleRegister = (password, name, phone) => {
		setGoogleLoginAttempt(false);
		onRegister(email, name, password, phone);
	};
	const handleEmailSubmit = useCallback(async (email, name) => {
		if (onHumanStatusRequest) await onHumanStatusRequest(email, name);
		setEmail(email);
		setName(name ?? "");
	}, [onHumanStatusRequest]);
	const handleRequestPasswordlessLogin = async () => {
		if (onPasswordlessLoginRequest) {
			setIsRequestingPasswordlessLogin(true);
			const { success } = await onPasswordlessLoginRequest(email);
			setIsRequestingPasswordlessLogin(false);
			setHasPasswordlessLogingRequestSucceeded(!!success);
		}
	};
	const resetValidEmail = (e) => {
		e?.preventDefault();
		setEmail("");
		setHasGeneratedPassword(false);
		setIsNewHuman(false);
	};
	const socialLoginErrorMessage = (provider, emailMismatch, email) => {
		if (emailMismatch && email && email !== "") return `You tried to sign in with an email that isn’t associated with this home address. Please sign in again with ${email}.`;
		return `${provider} authorization failed. Please try again or use the general login.`;
	};
	useEffect(() => {
		setEmail(initialEmail);
		/**
		* If an email was passed check its status so that we can know whether to show
		* the log in or sign up page
		*/
		if (initialEmail) handleEmailSubmit(initialEmail);
	}, []);
	useEffect(() => {
		setIsNewHuman(!!props.isNewHuman);
		setHasGeneratedPassword(!!props.hasGeneratedPassword);
	}, [props.hasGeneratedPassword, props.isNewHuman]);
	const updatingIsNewHuman = useRef(false);
	useEffect(() => {
		if (props.hasGeneratedPassword !== hasGeneratedPassword) setHasGeneratedPassword(!!props.hasGeneratedPassword);
		if (props.isNewHuman !== isNewHuman) {
			setIsNewHuman(!!props.isNewHuman);
			updatingIsNewHuman.current = true;
		}
	}, [
		initialEmail,
		props.hasGeneratedPassword,
		props.isNewHuman
	]);
	const hasEmailRegisterError = shouldRegisterOnEmailSubmit && !!emailRegistrationError;
	const hasSubmittedEmail = !!(email && email.length > 0 && !emailLoading && !hasEmailRegisterError);
	const skipPasswordPage = isNewHuman && hidePasswordField && fullNameHidden && phoneNumberInputHidden && shouldRegisterOnEmailSubmit && !!onAccountUpdate;
	useEffect(() => {
		if (updatingIsNewHuman.current) {
			updatingIsNewHuman.current = false;
			return;
		}
		if (hasSubmittedEmail && skipPasswordPage) onAccountUpdate(name, phone, false);
	}, [
		hasSubmittedEmail,
		name,
		onAccountUpdate,
		phone,
		skipPasswordPage
	]);
	if (hasSubmittedEmail && hasGeneratedPassword) return /* @__PURE__ */ React.createElement(AccountFormWrapper, { maxWidth: unsetContainer ? "100%" : 480 }, /* @__PURE__ */ React.createElement(CheckEmailForm, {
		analyticsName,
		email,
		hasGeneratedPassword,
		onResetValidEmail: resetValidEmail,
		requestResetPassword
	}));
	if (hasPasswordlessLoginRequestSucceeded !== void 0) return /* @__PURE__ */ React.createElement(PasswordlessResultPage, {
		hasSucceeded: hasPasswordlessLoginRequestSucceeded,
		email,
		analyticsName
	});
	const hasGoogleError = googleLoginAttempt && (emailMismatch || error);
	let loginErrorMessage = "";
	if (emailMismatch && email) loginErrorMessage = `You are logged in with an email that isn’t associated with this home address. Please sign in again with ${email}.`;
	else if (error && !googleLoginAttempt) if (error.includes("You have one more attempt before your account is locked.")) loginErrorMessage = "You’ll be locked out after this failed attempt.";
	else if (error.includes("Your account is locked.")) loginErrorMessage = "You’re locked out of your account due to the number of failed password attempts. Check your email to reset your password or try again in 1 hour.";
	else loginErrorMessage = "Invalid email or password";
	let headerText = loginHeaderText != null ? loginHeaderText : "Sign in or create an account";
	if (hasSubmittedEmail && isNewHuman) headerText = registrationHeaderText != null ? registrationHeaderText : "Finish creating an account";
	else if (hasSubmittedEmail) headerText = "Welcome back";
	const showGoogleAuthSection = showGoogleAuth && (!hasSubmittedEmail || overrideHasSubmittedEmailCheckForLogin);
	return /* @__PURE__ */ React.createElement(AccountFormWrapper, { maxWidth: unsetContainer ? "100%" : hasSubmittedEmail ? 560 : 400 }, headerText && /* @__PURE__ */ React.createElement(AccountFormHeader, null, headerText), !hasSubmittedEmail && disclaimerProps?.location !== "bottom" && /* @__PURE__ */ React.createElement(EmailDisclaimerText, { analyticsName }), /* @__PURE__ */ React.createElement(AccountFormSubHeader, {
		isNewHuman,
		email,
		hasSubmittedEmail,
		loginSubHeaderText,
		registrationSubHeaderText,
		onValidEmailReset: resetValidEmail,
		hidePasswordField,
		phoneNumberInputHidden
	}), showGoogleAuthSection && /* @__PURE__ */ React.createElement(Box, {
		justifyContent: "center",
		alignItems: "center",
		marginTop: headerText || loginSubHeaderText || registrationSubHeaderText ? "$20x" : 0
	}, /* @__PURE__ */ React.createElement(GoogleAuthRedirectButton, {
		analyticsPrefix: googleAuthAnalyticsPrefix,
		redirectTo: loginRedirectTo
	})), hasGoogleError && /* @__PURE__ */ React.createElement(ErrorPrompt, { message: socialLoginErrorMessage("Google", !!emailMismatch, email) }), showGoogleAuthSection && /* @__PURE__ */ React.createElement(Box, {
		gap: "$4x",
		marginTop: "$20x",
		alignItems: "center",
		justifyContent: "space-between",
		flexDirection: "row"
	}, /* @__PURE__ */ React.createElement(Box, { flex: 1 }, /* @__PURE__ */ React.createElement(Divider, null)), /* @__PURE__ */ React.createElement(Text, {
		typography: "$bodyMedium",
		tag: "p",
		color: "$contentTertiary",
		paddingHorizontal: 7
	}, "or"), /* @__PURE__ */ React.createElement(Box, { flex: 1 }, /* @__PURE__ */ React.createElement(Divider, null))), /* @__PURE__ */ React.createElement(FormWrapper, { marginTop: "$20x" }, hasSubmittedEmail && !skipPasswordPage ? /* @__PURE__ */ React.createElement(AccountPasswordForm, {
		analyticsName,
		email,
		name,
		phone,
		loading: passwordLoading,
		isNewHuman,
		loginError: loginErrorMessage,
		onLogin: handleLogin,
		onRegister: handleRegister,
		phoneNumberInputOptional: phoneNumberInputHidden ?? false,
		phoneNumberInputHidden,
		openForgotPasswordInNewTab,
		showPasswordlessLogin: !isNewHuman && !!onPasswordlessLoginRequest,
		onRequestPasswordlessLogin: handleRequestPasswordlessLogin,
		isRequestingPasswordlessLogin,
		hidePasswordField,
		onboardingAccountPageCTA,
		fullNamePlaceholder: fullNamePlaceholder ? fullNamePlaceholder : "",
		fullNameLabel: fullNameLabel ? fullNameLabel : "Full name",
		disclaimerProps,
		ref: passwordInputRef,
		passwordFormActionText,
		fullNameOptional,
		fullNameHidden
	}) : /* @__PURE__ */ React.createElement(Box, { gap: "$12x" }, /* @__PURE__ */ React.createElement(AccountEmailForm, {
		analyticsName,
		email,
		ref: emailInputRef,
		loading: emailLoading,
		onSubmit: handleEmailSubmit,
		openForgotPasswordInNewTab,
		showForgotPassword: !hidePasswordField
	}), disclaimerProps?.location === "bottom" && /* @__PURE__ */ React.createElement(EmailDisclaimerText, { analyticsName }))));
};
//#endregion
export { AccountForm as default };

